import BEM from '@polleverywhere/toolkit/lib/mixins/_bem'
import RegisterDispatchers from './dispatchers'
import User from '@polleverywhere/core/lib/models/user'
import Participant from '@polleverywhere/core/lib/models/participant'
import Session from '@polleverywhere/core/lib/models/session'
import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'
import { notify, clearAll } from './components/notification_manager'
import CookieConsentManager from './components/cookie_consent_manager'
import { getContent } from './pages'
import LayoutTabBar from './components/layout_tab_bar'
import { updateParticipantTheme } from './util/update_participant_theme'
import { getState } from './states'

function afterNonDefaultPageLoaded () {
  return new Promise(resolve => {
    const onPageLoaded = page => {
      if (page !== this.state.defaultPage) {
        this.state.off('page:loaded', onPageLoaded)
        setTimeout(resolve, 1000)
      }
    }
    this.state.on('page:loaded', onPageLoaded)
  })
}

function enableServiceWorker () {
  if ('serviceWorker' in navigator) {
    // Delay this to allow our initial page to load without delay due to precache setup
    Promise.race([
      afterNonDefaultPageLoaded.call(this),
      new Promise(resolve => setTimeout(resolve, 5000))
    ]).then(() => {
      navigator.serviceWorker.register('/service-worker.js').then(registration => {
        console.log('SW registered: ', registration)
      }).catch(registrationError => {
        console.log('SW registration failed: ', registrationError)
      })
    })
  }
}

function offlineError () {
  notify(this.el, {
    title: 'Error loading page',
    content: 'Please check your internet connection and try again',
    type: 'error',
    action: 'Reload page',
    onAction () { window.location.reload() }
  })
}

function unknownError () {
  notify(this.el, {
    title: 'Aww snap! Something broke',
    content: 'We\'ve been notified about this issue and we\'ll take a look at it shortly.',
    type: 'error',
    action: 'Reload page',
    onAction () { window.location.reload() }
  })
}

async function updatePage (state, page, options = {}) {
  try {
    const lastPage = state.previous('page') || state.defaultPage
    const content = await getContent.call(this, state, page, lastPage, options)
    content && showView.call(this, content)
  } catch (e) {
    // Unless it's the chunk loading error (network issue) let Rollbar know
    const chunkFailedMessage = /Loading.*chunk [\d]+ failed/
    if (chunkFailedMessage.test(e.message)) {
      offlineError.call(this)
    } else {
      unknownError.call(this)
      window.Rollbar?.error('Unable to update page', e)
    }
  }
}

function showView (view) {
  this.contentView && this.contentView.remove()
  this.contentView = view

  if (view.render) {
    this.$el.append(view.render().$el)
  } else {
    this.$el.append(view)
  }
}

function changeTheme () {
  const theme = this.state.get('settings').get('theme')

  this.$el.attr('data-theme', theme)
  this.trigger('change:theme', theme)
}

function startApp (options = {}) {
  // Get a new state
  resetState.call(this, options)

  // It would be nice if this could go into resetState, but in
  // the logout case, the route is not updated until after 'session:logout'
  // events are done (handled in routers/app.js).
  // Additionally, the state doesn't know it's route/page until after reset.
  if (this.options.shouldStartSessionOnStart) {
    // Pre-emptively load the default page
    this.state.changePage(this.state.defaultPage)

    // Start fetching profile data
    if (this.options.sessionAuthToken) {
      /*
     * Give the session an authentication token to use from the URL.
     * This is used by rails to forward on a session, usually for SSO purposes.
     * We need this as some browsers block third party cookies, and some apps are
     * on different domains
     *
     * WARNING: Don't use this token for anything but initial auth
     */
      this.state.get('session').startWithToken(this.options.sessionAuthToken)
    } else {
      this.state.get('session').start()
    }
  }

  this.state.on('change:role', async (state, role) => {
    // clear routers
    if (Backbone.history) {
      Backbone.history.handlers = []
    }

    this.$el.attr('data-role', role)

    await RegisterDispatchers(this)
    startApp.call(this, { role })
  })
}

function toggleReady () {
  this.$el.prop('hidden', !this.state.get('ready') && this.options.shouldStartSessionOnStart)
}

function resetState (opts = {}) {
  // Stop listening to old state
  if (this.state) {
    this.stopListening(this.state)
    this.state.off()
  }

  // create new state
  _.defaults(opts, {
    // instantiate user
    user: new User(),
    participant: new Participant(),
    role: opts.role
  })

  // Upgrade presenter account if necessary
  const upgradePresenter = opts.role === 'presenter'
  _.defaults(opts, {
    session: new Session(opts.user, opts.participant, { upgradePresenter }),
    mode: this.options.mode
  })

  // Setup layout tab bar
  LayoutTabBar.mode = this.options.mode
  LayoutTabBar.user = opts.user
  LayoutTabBar.allowRedirects = this.options.allowRedirects

  this.state = getState(opts)

  this.listenTo(this.state, 'change:theme', changeTheme.bind(this))
  this.listenTo(this.state, 'change:page', updatePage.bind(this))

  // Make sure we're visible if we're coming from initial load
  this.listenTo(this.state, 'change:ready', toggleReady.bind(this))
  toggleReady.call(this)

  this.listenTo(this.state, 'change:activePresenter', (state, activePresenter) => updateParticipantTheme(activePresenter, this.el))

  this.listenTo(this.state.get('user'), 'change:superuser', (user, superuser) => {
    const superuserWarningId = 'superuser-warning'

    if (superuser) {
      const superuserWarning = document.createElement('div')
      superuserWarning.id = superuserWarningId
      superuserWarning.innerHTML = `Superuser ${superuser.name} logged in as ${user.fullName()}. <a href="/logout">Please log out</a> when finished.`
      this.el.appendChild(superuserWarning)
    } else {
      const superuserWarning = document.getElementById(superuserWarningId)

      if (superuserWarning) {
        this.el.removeChild(superuserWarning)
      }
    }
  })

  this.listenTo(this.state, 'session:logout', () => {
    clearAll(this.el)

    this.$el.attr('data-theme', 'auto')
    this.trigger('change:theme', 'auto')

    // Initialize new state
    resetState.call(this, {
      role: this.state.get('role'),
      // app is fully loaded so no need to hide things
      ready: true
    })

    this.state.changePage(this.state.defaultPage)
  })

  this.listenTo(this.state, 'session:start session:anonymous', () => {
    // Start router if requested
    if (!this.options.router) {
      // Show default page
      this.state.changePage(this.state.defaultPage)
      return
    }

    this.cookieConsentManager?.showBannerUntilCookiesAccepted()

    // re-render the current page when logging in
    updatePage.call(this, this.state, this.state.get('page'))
  })

  this.listenTo(this.state, 'session:start', () => {
    if (['login', 'log in'].includes(this.state.get('page'))) {
      this.state.changePage(this.state.defaultPage)
    }
  })

  this.listenTo(this.state, 'session:start', () => {
    // Don't show welcome message in integration mode
    // TODO: find a better place for this. we may need a higher level
    // interstitial rendering mechanism
    if (this.options.mode !== 'integration' && this.state.get('role') === 'presenter' && this.state.messageHistory().shouldIntro('welcome')) {
      const onAction = () => {
        this.state.appStates.activities.startAction('create')
        this.state.messageHistory().trackIntro('welcome')
        return true
      }

      const onDismiss = () => {
        this.state.messageHistory().trackIntro('welcome')
      }

      notify(this.el, {
        title: 'Welcome!',
        content: 'Get started by creating an activity',
        type: 'info',
        action: 'Create activity',
        onAction,
        onDismiss
      })
    }
  })

  this.listenTo(this.state, 'notify:error', (type, originalError) => {
    if (type === 'offline') {
      offlineError.call(this)
    } else {
      unknownError.call(this)
    }
  })

  // Let consumers know we've reset the state
  this.trigger('reset:state', this.state)
}

/**
 * Main application View
 *
 * @event reset:state State was reset
 *  @property {Model} state New instance of state model
 *
 */
export default class App extends Backbone.View {
  get className () { return 'application' }

  get attributes () {
    return {
      hidden: 'hidden'
    }
  }

  async initialize (options = {}) {
    _.extend(this, BEM(this))
    this.options = _.defaults(options, {
      // Router is optional
      router: true,
      pushState: true,

      // allow signups
      allowSignups: true,
      // Allow reporting, disabled by apps like agave and slideware
      allowReports: true,
      // Allow trash
      allowTrash: true,

      // allow redirecting to other sites such as presenter.polleverywhere.com
      // certain platforms do not allow redirects such as Cordova
      // TODO: consolidate this into a higher level singleton settings model
      // so it doesn't need to be passed around
      allowRedirects: true,

      // pass 'integration' for when in a native container
      mode: 'standalone',

      // Enables additional logging
      debug: false,

      // Auth token used in SSO flows from rails - used to start up a pre-authed session
      sessionAuthToken: false,

      // enable/disable the session starting on startApp
      shouldStartSessionOnStart: true,

      // which role this app needs to run in. presenter|participant|viz|activity-renderer|agave
      role: 'presenter',

      // Should we try to install the service worker
      serviceWorker: true,

      // Accepted values:
      // false - disable all analytics tracking
      // 'consent' - requires explict consent via prompt
      // true - enables tracking without the need of consent
      allowAnalyticsTracking: true
    })

    const notificationManager = document.createElement('notification-manager')
    this.$el.append(notificationManager)
    this.$el.attr('data-role', options.role)

    await RegisterDispatchers(this)
    startApp.call(this, { role: this.options.role })

    if (this.options.serviceWorker) {
      enableServiceWorker.call(this)
    }

    if (CookieConsentManager.requiredFor(this.options.role, this.options.mode)) {
      this.cookieConsentManager = new CookieConsentManager({ app: this })
      this.$el.append(this.cookieConsentManager.render().$el)
      this.cookieConsentManager.once('allow_cookies', () => this.trigger('allow_cookies'))
    } else {
      this.trigger('allow_cookies')
    }
  }

  remove () {
    this.contentView && this.contentView.remove()

    if (this.state) {
      this.stopListening(this.state)
      this.state.off()
    }

    // clear routers
    if (Backbone.history) {
      Backbone.history.handlers = []
    }

    super.remove()
  }
}
